import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Lock,
  AlertTriangle,
  Bot,
  Calendar,
  FileText,
  AtSign,
  UserPlus,
  Mail,
  CheckCircle2,
  ShieldCheck,
  Globe,
} from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

// ---- Types ----

interface InboxItem {
  id: string;
  type: string;
  priority: string;
  title: string;
  subtitle: string;
  entityType: string;
  entityId: string;
  actionLabel: string;
  actionEndpoint: string | null;
  age: string;
  createdAt: string;
}

interface InboxResponse {
  items: InboxItem[];
  counts: { overdue: number; urgent: number; normal: number; info: number };
}

interface VisitorActivity {
  id: string;
  companyName: string | null;
  domain: string | null;
  pageUrl: string;
  visitorIp: string;
  occurredAt: string;
  accountId: string | null;
}

// ---- Constants ----

const TYPE_ICONS: Record<string, React.ReactNode> = {
  OVERDUE_TASK: <ClipboardList className="h-5 w-5" />,
  GATE_BLOCKER: <Lock className="h-5 w-5" />,
  STALE_OPPORTUNITY: <AlertTriangle className="h-5 w-5" />,
  AGENT_SUGGESTION: <Bot className="h-5 w-5" />,
  UPCOMING_CLOSE: <Calendar className="h-5 w-5" />,
  PENDING_PROPOSAL: <FileText className="h-5 w-5" />,
  MENTION: <AtSign className="h-5 w-5" />,
  NEW_LEAD: <UserPlus className="h-5 w-5" />,
  FOLLOW_UP_DRAFT: <Mail className="h-5 w-5" />,
  PENDING_APPROVAL: <ShieldCheck className="h-5 w-5" />,
};

const PRIORITY_COLORS: Record<string, { border: string; text: string }> = {
  RED: { border: 'border-l-[#EF4444]', text: 'text-[#EF4444]' },
  AMBER: { border: 'border-l-[#F59E0B]', text: 'text-[#F59E0B]' },
  GREEN: { border: 'border-l-[#10B981]', text: 'text-[#10B981]' },
};

interface FilterChip {
  label: string;
  types: string[] | null; // null = all
}

const FILTERS: FilterChip[] = [
  { label: 'All', types: null },
  { label: 'Overdue', types: ['OVERDUE_TASK'] },
  { label: 'Stale', types: ['STALE_OPPORTUNITY'] },
  { label: 'Agent', types: ['AGENT_SUGGESTION'] },
  { label: 'Tasks', types: ['OVERDUE_TASK', 'UPCOMING_CLOSE'] },
  { label: 'Leads', types: ['NEW_LEAD', 'FOLLOW_UP_DRAFT'] },
  { label: 'Proposals', types: ['PENDING_PROPOSAL'] },
  { label: 'Gates', types: ['GATE_BLOCKER'] },
  { label: 'Approvals', types: ['PENDING_APPROVAL'] },
];

// ---- Helpers ----

function entityRoute(entityType: string, entityId: string): string {
  switch (entityType) {
    case 'OPPORTUNITY':
      return `/opportunities/${entityId}`;
    case 'LEAD':
      return '/leads';
    case 'TASK':
      return '/tasks';
    case 'APPROVAL':
      return '/inbox'; // approval handled inline
    default:
      return '/dashboard';
  }
}

// ---- Visitor Activity Panel (Story 80.3) ----

function VisitorActivityPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState<VisitorActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<VisitorActivity[]>('/intent/visitor-activity')
      .then(({ data }) => setVisitors(data))
      .catch(() => setVisitors([]))
      .finally(() => setLoading(false));
  }, []);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function convertToLead(visitor: VisitorActivity) {
    try {
      await apiClient.post('/intent/visitor-activity/convert', {
        visitorActivityId: visitor.id,
        domain: visitor.domain,
        companyName: visitor.companyName,
        accountId: visitor.accountId,
      });
      showToast(t('visitorActivity.converted'));
      setVisitors((prev) => prev.filter((v) => v.id !== visitor.id));
    } catch {
      showToast(t('visitorActivity.convert_failed'));
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">{t('inbox.loading')}</p>;

  if (visitors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Globe className="mb-3 h-12 w-12" />
        <p className="text-sm">{t('visitorActivity.empty')}</p>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}
      <div className="space-y-3">
        {visitors.map((visitor) => (
          <div
            key={visitor.id}
            className="flex items-center gap-4 rounded-lg border bg-card p-4"
          >
            <div className="shrink-0 text-muted-foreground">
              <Globe className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {visitor.companyName ?? visitor.domain ?? visitor.visitorIp}
              </p>
              <p className="text-sm text-muted-foreground truncate">{visitor.pageUrl}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(visitor.occurredAt).toLocaleString()}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {visitor.accountId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/accounts/${visitor.accountId}`)}
                >
                  {t('visitorActivity.view_account')}
                </Button>
              )}
              <Button size="sm" onClick={() => convertToLead(visitor)}>
                {t('visitorActivity.convert_to_lead')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ---- Component ----

export function InboxPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [counts, setCounts] = useState<InboxResponse['counts']>({
    overdue: 0,
    urgent: 0,
    normal: 0,
    info: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inbox' | 'visitors'>('inbox');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<InboxResponse>('/inbox');
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCounts(data?.counts ?? { overdue: 0, urgent: 0, normal: 0, info: 0 });
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleAction(item: InboxItem) {
    if (item.actionEndpoint) {
      try {
        await apiClient.post(item.actionEndpoint);
        showToast(`${item.actionLabel} ${t('inbox.completed')}`);
        setItems((prev) => prev.filter((i) => i.id !== item.id));
      } catch {
        showToast(t('inbox.action_failed'));
      }
    } else {
      navigate(entityRoute(item.entityType, item.entityId));
    }
  }

  async function handleApprovalDecision(
    item: InboxItem,
    decision: 'APPROVED' | 'REJECTED',
  ) {
    let note: string | undefined;
    if (decision === 'REJECTED') {
      note = window.prompt(t('inbox.rejection_note')) ?? undefined;
    }
    try {
      await apiClient.post(`/chain-approvals/${item.entityId}/decide`, {
        decision,
        note: note ?? null,
      });
      showToast(decision === 'APPROVED' ? t('inbox.approved') : t('inbox.rejected'));
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch {
      showToast(t('inbox.action_failed'));
    }
  }

  const filterDef = FILTERS[activeFilter];
  const visibleItems = filterDef.types
    ? items.filter((i) => filterDef.types!.includes(i.type))
    : items;

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('inbox.title')}</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {counts.overdue > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-[#EF4444]" />
              {counts.overdue} {t('inbox.overdue')}
            </span>
          )}
          {counts.urgent > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-[#F59E0B]" />
              {counts.urgent} {t('inbox.urgent')}
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-[#10B981]" />
            {counts.normal + counts.info} {t('inbox.items')}
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'inbox'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('inbox')}
        >
          {t('inbox.tab_inbox')}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'visitors'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('visitors')}
        >
          {t('inbox.tab_visitors')}
        </button>
      </div>

      {activeTab === 'visitors' && <VisitorActivityPanel />}

      {activeTab === 'inbox' && (
        <>
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f, idx) => (
              <Button
                key={f.label}
                variant={activeFilter === idx ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(idx)}
              >
                {f.label}
              </Button>
            ))}
          </div>

          {/* Loading */}
          {loading && items.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('inbox.loading')}</p>
          )}

          {/* Feed */}
          {!loading && visibleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CheckCircle2 className="mb-3 h-12 w-12" />
              <p className="text-sm">{t('inbox.empty')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleItems.map((item) => {
                const colors = PRIORITY_COLORS[item.priority] ?? PRIORITY_COLORS.GREEN;
                const icon = TYPE_ICONS[item.type] ?? <ClipboardList className="h-5 w-5" />;

                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-4 rounded-lg border border-l-4 bg-card p-4 ${colors.border}`}
                  >
                    {/* Type icon */}
                    <div className="shrink-0 text-muted-foreground">{icon}</div>

                    {/* Content */}
                    <div
                      className="min-w-0 flex-1 cursor-pointer"
                      onClick={() =>
                        navigate(entityRoute(item.entityType, item.entityId))
                      }
                    >
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.subtitle}</p>
                    </div>

                    {/* Age + action */}
                    <div className="flex shrink-0 items-center gap-3">
                      <span className={`text-xs font-medium ${colors.text}`}>
                        {item.age}
                      </span>
                      {item.type === 'PENDING_APPROVAL' ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleApprovalDecision(item, 'APPROVED')
                            }
                          >
                            {t('inbox.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              handleApprovalDecision(item, 'REJECTED')
                            }
                          >
                            {t('inbox.reject')}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => handleAction(item)}>
                          {item.actionLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
